import {useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  AlertCircle,
  Building2,
  ChevronsDownUp,
  ChevronsUpDown,
  ClipboardList,
  Package,
  RefreshCw,
  ShoppingCart,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import {PurchaseOrderCNPJSection} from '../components/PurchaseOrderCNPJSection'
import {createPreOrder} from '../services/purchaseOrdersApi'
import {useProductsGrouped} from '../shared/hooks/useProducts'
import {SellerPageLoader} from '../shared/components/SellerPageLoader'
import {CardHelp} from '../shared/components/CardHelp'

const CURRENCY_FORMATTER = new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'})

export function PurchaseOrderPage() {
  const navigate = useNavigate()
  const {groups, allProducts, loading, error, refetch} = useProductsGrouped()
  const [expandedCNPJs, setExpandedCNPJs] = useState<Set<string>>(new Set())
  const [creatingCnpj, setCreatingCnpj] = useState('')
  const [actionError, setActionError] = useState('')

  const cnpjList = useMemo(() => groups.map((group) => group.cnpj), [groups])

  useEffect(() => {
    if (groups.length === 1) {
      setExpandedCNPJs(new Set([groups[0].cnpj]))
    } else if (groups.length > 0 && expandedCNPJs.size === 0) {
      setExpandedCNPJs(new Set([groups[0].cnpj]))
    }
  }, [groups])

  const globalTotals = useMemo(() => {
    const items = allProducts.filter((product) => product.pedidoSugerido > 0)
    return {
      items: items.length,
      units: items.reduce((sum, product) => sum + Math.ceil(product.pedidoSugerido), 0),
      urgent: items.filter((product) => product.status === 'ruptura' || product.status === 'iminente').length,
      medium: items.filter((product) => product.status === 'minimo' || product.status === 'critica').length,
      investment: items.reduce((sum, product) => sum + Math.ceil(product.pedidoSugerido) * (product.precoUnitario ?? 0), 0),
      pricedItems: items.filter((product) => product.precoUnitario !== undefined).length,
    }
  }, [allProducts])

  const handleToggle = (cnpj: string) => {
    setExpandedCNPJs((current) => {
      const next = new Set(current)
      if (next.has(cnpj)) next.delete(cnpj)
      else next.add(cnpj)
      return next
    })
  }

  const handleCreatePreOrder = async (cnpj: string, razaoSocial: string) => {
    setCreatingCnpj(cnpj)
    setActionError('')
    try {
      const created = await createPreOrder({cnpj, razao_social: razaoSocial || undefined})
      navigate(`/lojistas/pedidos/${created.order.order_id}`)
    } catch (requestError) {
      setActionError(requestError instanceof Error ? requestError.message : 'Não foi possível criar o pré-pedido.')
    } finally {
      setCreatingCnpj('')
    }
  }

  if (loading) {
    return (
      <SellerPageLoader
        title='Preparando o pedido sugerido'
        description='Reunindo produtos, prioridades e quantidades recomendadas.'
      />
    )
  }

  if (error) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <div className='max-w-md text-center'>
          <span className='mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600'><AlertCircle className='h-6 w-6' /></span>
          <p className='font-semibold text-gray-900'>Erro ao carregar dados</p>
          <p className='mt-1 text-sm text-gray-500'>{error}</p>
          <button onClick={refetch} className='mt-4 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700'><RefreshCw className='h-4 w-4' />Tentar novamente</button>
        </div>
      </div>
    )
  }

  const allExpanded = cnpjList.length > 0 && expandedCNPJs.size === cnpjList.length

  return (
    <div>
      <div className='mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>Pedido de Compra Sugerido</h1>
          <p className='mt-2 text-gray-600'>Crie um pré-pedido persistido antes de revisar e finalizar as quantidades.</p>
        </div>
        <div className='flex items-center gap-3'>
          <button onClick={() => navigate('/lojistas/pedidos')} className='inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50'>
            <ClipboardList className='h-4 w-4' />
            Ver pré-pedidos e pedidos
          </button>
          <button onClick={refetch} className='inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50'>
            <RefreshCw className='h-4 w-4' />
            Atualizar
          </button>
        </div>
      </div>

      {actionError && (
        <div className='mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
          <AlertCircle className='mt-0.5 h-4 w-4 flex-none' />
          <span>{actionError}</span>
          <button type='button' onClick={() => setActionError('')} className='ml-auto font-semibold'>Fechar</button>
        </div>
      )}

      <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-3 xl:grid-cols-4'>
        <div className='relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm'>
          <CardHelp content='Produtos cuja quantidade sugerida de reposição é maior que zero.' className='absolute right-2 top-2' />
          <div className='flex items-center justify-between pr-7'><div><p className='text-sm font-medium text-gray-600'>Itens para reposição</p><p className='mt-2 text-3xl font-bold text-gray-900'>{globalTotals.items}</p></div><span className='rounded-full bg-blue-100 p-3 text-blue-700'><Package className='h-6 w-6' /></span></div>
        </div>
        <div className='relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm'>
          <CardHelp content='Soma de todas as quantidades sugeridas para os produtos listados.' className='absolute right-2 top-2' />
          <div className='flex items-center justify-between pr-7'><div><p className='text-sm font-medium text-gray-600'>Total de unidades</p><p className='mt-2 text-3xl font-bold text-gray-900'>{globalTotals.units}</p></div><span className='rounded-full bg-teal-100 p-3 text-teal-700'><TrendingUp className='h-6 w-6' /></span></div>
        </div>
        <div className='relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm'>
          <CardHelp content='Produtos em ruptura instalada ou ruptura iminente, classificados com prioridade alta.' className='absolute right-2 top-2' />
          <div className='flex items-center justify-between pr-7'><div><p className='text-sm font-medium text-gray-600'>Urgentes</p><p className='mt-2 text-3xl font-bold text-gray-900'>{globalTotals.urgent}</p><p className='mt-1 text-xs text-gray-500'>{globalTotals.medium} prioridades médias</p></div><span className='rounded-full bg-red-100 p-3 text-red-700'><AlertCircle className='h-6 w-6' /></span></div>
        </div>
        {globalTotals.pricedItems > 0 && (
          <div className='relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm'>
            <CardHelp content={`Investimento calculado com o custo médio disponível para ${globalTotals.pricedItems} de ${globalTotals.items} itens.`} className='absolute right-2 top-2' />
            <div className='flex items-center justify-between pr-7'><div><p className='text-sm font-medium text-gray-600'>Investimento estimado</p><p className='mt-2 text-2xl font-bold text-gray-900'>{CURRENCY_FORMATTER.format(globalTotals.investment)}</p><p className='mt-1 text-xs text-gray-500'>{globalTotals.pricedItems} itens com custo</p></div><span className='rounded-full bg-violet-100 p-3 text-violet-700'><Wallet className='h-6 w-6' /></span></div>
          </div>
        )}
      </div>

      <div className='mb-5 flex items-center justify-between'>
        <div className='flex items-center gap-2 text-sm text-gray-600'><Building2 className='h-4 w-4 text-gray-400' /><strong>{groups.length}</strong><span>{groups.length === 1 ? 'CNPJ' : 'CNPJs'}</span><span className='mx-1 text-gray-300'>|</span><strong>{globalTotals.items}</strong><span>itens para reposição</span></div>
        {groups.length > 1 && (
          <div className='flex items-center gap-2'>
            <button onClick={() => setExpandedCNPJs(new Set(cnpjList))} disabled={allExpanded} className='inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-40'><ChevronsUpDown className='h-4 w-4' />Expandir todos</button>
            <button onClick={() => setExpandedCNPJs(new Set())} disabled={expandedCNPJs.size === 0} className='inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-40'><ChevronsDownUp className='h-4 w-4' />Ocultar todos</button>
          </div>
        )}
      </div>

      {globalTotals.items === 0 ? (
        <div className='rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm'><ShoppingCart className='mx-auto mb-4 h-16 w-16 text-gray-300' /><h3 className='text-xl font-semibold text-gray-900'>Nenhum pedido necessário</h3><p className='mt-2 text-gray-600'>Todos os produtos estão com estoque adequado no momento.</p></div>
      ) : (
        <div className='space-y-4'>
          {groups.map((group) => (
            <PurchaseOrderCNPJSection
              key={group.cnpj}
              cnpj={group.cnpj}
              razaoSocial={group.razaoSocial}
              products={group.products}
              isExpanded={expandedCNPJs.has(group.cnpj)}
              onToggle={() => handleToggle(group.cnpj)}
              onCreatePreOrder={() => handleCreatePreOrder(group.cnpj, group.razaoSocial)}
              creatingPreOrder={creatingCnpj === group.cnpj}
              createDisabled={Boolean(creatingCnpj)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
